import { __decorate, __metadata } from "tslib";
import { I18nThemed } from 'interfaces/mixins';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { w, v } from '@dojo/framework/core/vdom';
import { storeInject } from '@dojo/framework/stores/StoreInjector';
import { beforeProperties } from '@dojo/framework/core/decorators/beforeProperties';
import { lccstore, userstore, load_topic, keymap, pathmap, quizmap } from 'lcc-state';
import { convertToBoolean } from '@holmescorp/common';
import { LRUCache } from 'collections';
import { RelatedTopics } from 'related-topics';
import { BackView } from '@holmescorp/backview';
import { Icon } from '@holmescorp/base-widgets';
import { GotIt } from './gotit';
import { Keys } from '@holmescorp/common';
import css from '../css/topicpage.m.css';
const topicLRU = new LRUCache({ maxlength: 3 });
let TopicPage = class TopicPage extends I18nThemed {
    lastTopic = false;
    nextTopic = (current) => {
        const { messages } = this.get_nls();
        const quizText = messages['quiz'] || '';
        const status = userstore.get(userstore.path('status'));
        const { next, parent = '' } = status[current];
        if (next === null) {
            this.lastTopic = true;
            const { path: tgpath } = keymap.get(parent);
            const [, explore, , tgf] = tgpath && tgpath.split('/');
            const path = `/${explore}`;
            const { title } = keymap.get(tgf);
            const { quiz } = quizmap.get(parent) || {};
            if (quiz) {
                return {
                    title: quizText,
                    path: `${tgpath}/quiz`
                };
            }
            else {
                return {
                    title,
                    path
                };
            }
        }
        this.lastTopic = false;
        const { title, path } = keymap.get(next) || { title: '', path: '' };
        return { title, path };
    };
    previousTopic = (current) => {
        const status = userstore.get(userstore.path('status'));
        const { previous } = status[current];
        if (previous === null) {
            return {
                title: '',
                path: null
            };
        }
        const { title, path } = keymap.get(previous);
        return { title, path };
    };
    get_nls() {
        const { dynls } = this.properties;
        const bundle = dynls.get('ditabook');
        if (!bundle) {
            return { isPlaceholder: true, format: () => '', messages: {} };
        }
        return this.localizeBundle(bundle);
    }
    get_settings_nls() {
        const { dynls } = this.properties;
        const bundle = dynls.get('settings');
        if (!bundle) {
            return { isPlaceholder: true, format: () => '', messages: {} };
        }
        return this.localizeBundle(bundle);
    }
    setAddlProperties(properties) {
        const { get, path } = userstore;
        properties.status = get(path('status'));
        return properties;
    }
    next_prev(currentTopic, keyval) {
        const next = this.nextTopic(currentTopic);
        const prev = this.previousTopic(currentTopic);
        return v('div', { key: `${keyval}-prev-next-block`, classes: [this.theme(css.nextprev)] }, [
            prev.path !== null
                ? v('div', {
                    tabIndex: 0,
                    key: 'prev-topic',
                    xqa: 'action prev',
                    role: 'button',
                    classes: [this.theme(css.previous)],
                    onclick: (event) => {
                        event && event.stopPropagation();
                        const { view } = pathmap.get(prev.path);
                        view();
                    },
                    onkeydown: (event) => {
                        const { view } = pathmap.get(prev.path);
                        switch (event.key) {
                            case Keys.Enter:
                            case Keys.Space:
                            case Keys.Spacebar:
                                event.preventDefault();
                                view();
                        }
                    }
                }, [w(Icon, { faname: 'angle-left', size: 'sm' }), v('span', {}, [prev.title])])
                : v('div', { key: 'prev-topic', classes: [this.theme(css.previous)] }),
            v('div', {
                tabIndex: 0,
                key: 'next-topic',
                xqa: 'action next',
                role: 'button',
                classes: [this.theme(css.next)],
                onclick: (event) => {
                    event && event.stopPropagation();
                    const { view } = pathmap.get(next.path);
                    view();
                },
                onkeydown: (event) => {
                    const { view } = pathmap.get(next.path);
                    switch (event.key) {
                        case Keys.Enter:
                        case Keys.Space:
                        case Keys.Spacebar:
                            event.preventDefault();
                            view();
                    }
                }
            }, [
                v('span', {}, [next.title]),
                w(Icon, { faname: this.lastTopic ? 'angle-double-right' : 'angle-right', size: 'sm' })
            ])
        ]);
    }
    render_body(rel) {
        if (!topicLRU.has(rel)) {
            const { get, path } = lccstore;
            const bookversion = get(path('bookversion'));
            const lang = window.hcstatic.portal_language || 'en';
            load_topic(rel, bookversion, lang).then((topic) => {
                topicLRU.add(rel, topic);
                this.invalidate();
            });
            return v('div', { key: `${rel}-body`, classes: [this.theme(css.loading)] }, [
                v('i', { classes: ['fa', 'fa-spinner', 'fa-spin'] })
            ]);
        }
        else {
            const topic = topicLRU.get(rel);
            const { messages: { topic_skip_title = 'false' } } = this.get_nls();
            const skipTitle = convertToBoolean(topic_skip_title);
            return w('Generator', {
                key: `${rel}-body`,
                skipTitle,
                renderBody: true,
                topic: topic
            });
        }
    }
    render() {
        const { isPlaceholder, messages: { reading = '[ditabook] reading', reading_show_title = 'true' } } = this.get_nls();
        if (isPlaceholder) {
            return null;
        }
        const { rel, title, status } = this.properties;
        const setPageTitle = () => {
            const { association } = this.get_settings_nls().messages;
            const { get, path } = lccstore;
            const product = get(path('product'));
            document.title = `${reading} - ${title} (${association} ${product.toLocaleUpperCase()} )`;
        };
        const { is_demo = false, demo_allowed_topics = '[]' } = this.get_settings_nls().messages;
        const parentRel = status[rel].parent ? `${status[rel].parent}` : '';
        let allowed_topics = [];
        try {
            allowed_topics = JSON.parse(demo_allowed_topics);
        }
        catch (e) {
            console.warn(`json parse error ${e}`, demo_allowed_topics);
        }
        const isLocked = is_demo && allowed_topics.indexOf(parentRel) === -1 && allowed_topics.indexOf(rel) === -1;
        if (isLocked) {
            const { view } = pathmap.get('/');
            view();
        }
        setPageTitle();
        const { messages: { hasRelatedTopics = undefined } } = this.get_settings_nls();
        return v('div', { key: `${rel}`, xqa: `topic ${rel}`, classes: [this.theme(css.root)] }, [
            v('div', { key: `${rel}-title`, classes: [this.theme(css.titleBlock)] }, [
                v('h1', { classes: [this.theme(css.title)] }, [title]),
                w(BackView, {})
            ]),
            this.next_prev(rel, 'top'),
            w(GotIt, {}),
            this.render_body(rel),
            w(GotIt, {}),
            this.next_prev(rel, 'bot'),
            hasRelatedTopics && Boolean(parseInt(hasRelatedTopics)) ? w(RelatedTopics, { topicid: rel }) : null
        ]);
    }
};
__decorate([
    beforeProperties(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Object)
], TopicPage.prototype, "setAddlProperties", null);
TopicPage = __decorate([
    theme(css),
    storeInject({
        name: 'lcc-state',
        paths: [['dynls']],
        getProperties: (store) => {
            const { get, path } = store;
            return {
                dynls: get(path('dynls'))
            };
        }
    })
], TopicPage);
export { TopicPage };
